import type { DraftMode, Folder, MailAction } from '@jerome2/shared';
import { mailMenuItems, type MailMenuTarget, type MenuEntry, type MenuItem } from './ContextMenu.tsx';

// Werkbalk van de lezer (desktop): de vaste knoppen en het menu Meer, gemaakt uit dezelfde items als
// het rechtsklikmenu (mailMenuItems), zodat teksten en gedrag op beide plekken gelijk blijven.
// Openen valt weg (de mail staat al open); de knoppen houden hun sneltoets in het label. Een concept
// krijgt het korte conceptmenu; Verzenden en Bewerken staan dan al in de balk boven het concept.

export interface ToolbarTarget extends MailMenuTarget {
  webLink?: string | null | undefined;
}

export interface ToolbarHandlers {
  compose: (mode: DraftMode) => void;
  action: (action: MailAction, folderId?: string, folderName?: string) => void;
  /** Zonder deze callback geen item Naar/Uit einde dag. */
  eod?: ((mark: boolean) => void) | undefined;
  /** Zonder deze callback geen item Instructie geven. */
  instruct?: (() => void) | undefined;
}

/** De knoppen in deze volgorde, met de sneltoets uit MailScreen. */
const PRIMARY: Array<[id: string, key: string]> = [
  ['reply', 'r'],
  ['replyAll', 'a'],
  ['forward', 'f'],
  ['archive', 'e'],
  ['delete', '#'],
  ['flag', 's'],
];
/** De groepen onder Meer, met een lijn ertussen; Open in Outlook komt er als laatste groep bij. */
const MORE: string[][] = [
  ['move', 'read'],
  ['eod', 'instruct'],
];

export function toolbarItems(detail: ToolbarTarget, folders: Folder[], on: ToolbarHandlers): { primary: MenuItem[]; more: MenuEntry[] } {
  const none = () => undefined;
  const entries = mailMenuItems(detail, folders, { open: none, compose: on.compose, action: on.action, eod: on.eod ?? none, instruct: on.instruct ?? none });
  const byId = new Map(entries.filter((e): e is MenuItem => e !== 'separator').map((it) => [it.id, it]));
  if (!on.eod) byId.delete('eod');
  if (!on.instruct) byId.delete('instruct');
  const primary = PRIMARY.flatMap(([id, key]) => {
    const it = byId.get(id);
    return it ? [{ ...it, label: `${it.label} (${key})` }] : [];
  });
  const groups = MORE.map((ids) => ids.flatMap((id) => byId.get(id) ?? []));
  if (detail.webLink) groups.push([{ id: 'outlook', label: 'Open in Outlook', icon: 'external', href: detail.webLink }]);
  const more = groups.filter((g) => g.length).flatMap((g, i): MenuEntry[] => (i ? ['separator', ...g] : g));
  return { primary, more };
}
